import { resetPassword } from 'aws-amplify/auth';
import NavBackButton from 'components/buttons/NavBackButton';
import { FormCard } from 'components/forms/FormComponents';
import useNeveForm from 'components/forms/NeveForm';
import TextField from 'components/forms/TextField';
import React from 'react';
import { requiredFieldErrorMessage } from 'shared/error-messages';
import { HomepageContext } from './Homepage';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import strings from 'strings/strings.json';
import { FlexCol, Headline2 } from 'components/Elements';
import Button from 'components/buttons/Button';

type ResetPasswordFormInput = {
    emailAddress: string;
};

export function ResetPasswordForm(): JSX.Element {
    const { setState, setSharedData, sharedData } = React.useContext(HomepageContext);

    const [emailingResetCode, setEmailingResetCode] = React.useState(false);
    const [error, setError] = React.useState('');

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useNeveForm<ResetPasswordFormInput>({
        emailAddress: sharedData.emailAddress,
    });

    async function submitResetPassword(data: ResetPasswordFormInput) {
        try {
            setEmailingResetCode(true);
            await resetPassword({ username: data.emailAddress });
            setSharedData((prev) => ({ ...prev, emailAddress: data.emailAddress, password: '' }));
            setState('confirmResetPassword');
        } catch (error: any) {
            switch (error.name) {
                case 'UserNotFoundException':
                    setError(strings.homepage.errors.resetPassword.userNotFoundException);
                    break;
                case 'UserNotConfirmedException':
                    setError(strings.homepage.errors.resetPassword.userNotConfirmedException);
                    break;
                case 'NotAuthorizedException':
                    setError(strings.homepage.errors.resetPassword.notAuthorizedException);
                    break;
                case 'LimitExceededException':
                    setError(strings.homepage.errors.resetPassword.limitExceededException);
                    break;
                default:
                    setError(strings.homepage.errors.resetPassword.general);
                    break;
            }
        } finally {
            setEmailingResetCode(false);
        }
    }

    return (
        <FormCard onSubmit={handleSubmit(submitResetPassword)}>
            <FlexCol>
                <NavBackButton
                    backButtonTitle={''}
                    backButtonAria={strings.homepage.buttons.signIn}
                    backButtonHref={''}
                    buttonFunctionOverride={() => {
                        setState('signIn');
                    }}
                />
                <Headline2>{strings.homepage.headlines.resetPassword}</Headline2>
            </FlexCol>

            <Notification
                title={strings.homepage.resetPasswordNotification}
                notificationType={NotificationType.INFO}
                maxWidth={'100%'}
            />
            <TextField
                fieldName={strings.homepage.fields.email}
                labelText={strings.homepage.fields.email}
                inputAria={strings.homepage.fields.email}
                required
                errorMessage={errors.emailAddress?.message}
                {...register('emailAddress', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.email),
                    },
                })}
            />
            <Button
                title={strings.homepage.buttons.sendResetCode}
                aria={strings.homepage.buttons.sendResetCode}
                type="submit"
                fullwidth
                submitting={emailingResetCode}
            />
            {error && <Notification title={error} notificationType={NotificationType.DANGER} maxWidth={'100%'} />}
        </FormCard>
    );
}
